/**
 * Created by Thomas Mark
 * Date: 2019-10-29
 * 
 * Portfolios verwalten
 * 
 * Der PortfoliosController enthält die Hauptsteuerung der Anwendung
 * 
 * Folgende Komponenten/Funktionen stehen hier zur Verfügung:
 * - Header - Kopfzeile mit Navigation
 * - Portfolio - Grid/Kachel-System mit allen Mappen
 * - GroupController - Vertikale Liste zur Navigation durch die Produktgruppen eines Portfolios
 * - ProduktController - Vertikale und Horizontale Liste zur Navigation durch alle Produkte einer Produktgruppe 
 * - ProduktPortrait - Produktdetails im Portrait-Format
 * - ProduktLandscape - Produktdetails im Landscape-Format
 * - SearchController - Suchfunktion für Produktsuche
 * - FilterModal - Filterfunktion zur Eingrenzung/Anzeige aller Produkte 
 * - FavoredController - Favoritenfunktion zur Markierung beliebter Produkte
 * - Footer - Fussleiste mit Steuerung der Hauptfunktionen
 * 
 * Zudem werden hier folgende Hilfsdialoge zur Verfügung gestellt:
 * - MediaModal - Dialog zur Anzeige von PDF-Dokumenten oder Videos
 * - WebsiteModal - Dialog zur Anzeige eine Website.
 * 
 * Im ADMIN-Modus können hier Portfolios, Produktgruppen und Produkte verwaltet werden.
 * Hierzu stehen folgende Dialoge zur Verfügung:
 * - PortfolioModal - Pflege/Verwaltung der Portfolios
 * - GroupModal - Pflege/Verwaltung der Produktgruppen
 * - ProductModal - Pflege/Verwaltung einzelner Produkte
 * - ProductTagsModal - Pflege/Verwaltung der Produkt-Schlagwörter
 * - ProductDetailsModal - Pflege/Verwaltung der Produktdetails
 * - DebugModal - Hilfsdialog zur Anzeige der JSON-Struktur eines Portfolios
 * 
 * Eine zentrale Aufgabe im PortfolioController übernimmt die Funktion loadContainer()
 * Diese Funktion lädt alle Daten eines Containers aus dem lokalen nichtflüchtigen 
 * Speicher (IndexDB oder cordova-plugin-file) in den lokalen flüchtigen Speicher 
 * (REDUX-Store) der Anwendung.
 * 
 * 
 */
import React from 'react';
import { Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import Header from "../../partials/Header";
import Footer from "../../partials/Footer";
import UploadModal from "../../partials/UploadModal";
import { ConfirmModal } from "../../partials/ConfirmModal";
import Portfolio from "./Portfolio";
import FavoredController from "./FavoredController";

import GroupController from "../group/GroupController";
import ProductControllerUser from "../product/ProductControllerUser";
import ProductControllerAdmin from "../product/ProductControllerAdmin";

import ProductPortrait from "../product/ProductPortrait";
import ProductLandscape from "../product/ProductLandscape";

import SplashScreen from "./SplashScreen";
import SearchController from "./SearchController";
import FilterModal from "./FilterModal";

import ProgressBar from "../../partials/ProgressBar";
import ProgressView from "../../partials/ProgressView";
import DebugModal from "./DebugModal";
import MediaModal from "./MediaModal";
import GroupModal from "../group/GroupModal";
import WebsiteModal from "../sites/WebsiteModal";
import ImageTagsModal from "../imageGallery/ImageTagsModal";
import ImageGalleryModal from "../imageGallery/ImageGalleryModal";
import ProductModal from "../product/ProductModal";
import PortfolioModal from "./PortfolioModal";
import ProductTagsModal from "../product/ProductTagsModal";
import ProductDetailsModal from "../product/ProductDetailsModal";
import DownloadConfirmModal from "../containers/DownloadConfirmModal";

import * as utils from "../../../basics/utils";
import * as cordova from "../../../basics/cordova";
import * as appActions from "../../../store/actions/appActions";
import * as userActions from "../../../store/actions/userActions";
import * as iconActions from "../../../store/actions/iconActions";
import * as syncActions from "../../../store/actions/syncActions";
import * as versionActions from "../../../store/actions/versionActions";
import * as settingActions from "../../../store/actions/settingActions";
import * as portfolioActions from "../../../store/actions/portfolioActions";
import * as containerActions from "../../../store/actions/containerActions";

class PortfoliosController extends React.Component {
    constructor(props) {
        super(props);

        this.debug = false;

        this.component = 'PortfoliosController';

        this.timeout = null;

        this.containerListExists = false;
    }

    // ==========================================================================
    //
    // LIFESYCLE - SECTION
    //
    // ==========================================================================

    componentDidMount = async () => {
        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:Mount`);
        debug && console.log(`##################################################\n\n`);

        await cordova.createLocalDirectory('image');
        await cordova.createLocalDirectory('pdf');
        await cordova.createLocalDirectory('video');

        /**
         * containers.json aus IndexedDB lesen
         * 
         * Hier wird geprüft, ob die 'containers.json' schon existiert.
         */
        await cordova.readLocalJSON(null, 'containers.json').then((containersJSON) => {
            debug && console.log('   readLocalJSON(containers.json) success:', containersJSON);
            this.containerListExists = true;
        }).catch(async (error) => {
            debug && console.log('   readLocalJSON(containers.json): Datei existiert noch nicht!', error);
            this.containerListExists = false;
        });

        // = LOAD CONTAINER =========================================================
        // 
        // ==========================================================================
        this.loadCurrentContainer(true);

        // ==========================================================================
        // 
        // NETZWERK-Verbindung managen
        // 
        // ==========================================================================
        /*
        
        if (window['cordova']) {
            if (navigator['connection'] && navigator['connection']['type']) {
                console.log('################################');
                console.log('CONNECTION type:', navigator.connection.type);
                console.log('################################');

                if (navigator.connection.type === 'none') {
                    this.props.setOnline(false);
                } else {
                    this.props.setOnline(true);
                }
            }
        }
        */

        // = READY =================================================================
        // Erst hier ist die Anwendung fertig geladen
        // =========================================================================
        this.props.setPortfoliosReady(true);
    }

    componentDidUpdate(prevProps) {
        const { currentContainer, appPreview } = this.props.app;

        if (currentContainer !== prevProps.app.currentContainer) {
            console.log(`PortfoliosController:componentDidUpdate(${currentContainer}/${prevProps.app.currentContainer})`);
            this.loadCurrentContainer(true); // forceReload=false
        }

        if (appPreview !== prevProps.app.appPreview) {
            console.log(`PortfoliosController:componentDidUpdate(${appPreview}/${prevProps.app.appPreview})`);
            this.loadCurrentContainer(true); // forceReload=true
        }
    }

    componentWillUnmount = () => {
        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:Unmount`);
        debug && console.log(`##################################################\n\n`);

        if (this.timeout) { clearTimeout(this.timeout) }
    }

    // ==========================================================================
    //
    // CONTAINER - SECTION
    //
    // ==========================================================================

    /**
     * Diese Funktion sorgt dafür, dass sämtliche Daten des aktuellen Containers
     * in den flüchtigen REDUX-Store übernommen werden.
     *
     * @param  {boolean} bForceReload - Erneutes laden erzwingen
     */
    loadCurrentContainer = async (bForceReload) => {
        const { appPreview, currentContainer, imageBuffer } = this.props.app;
        const { portfolioList } = this.props.portfolio;
        const { loginUser } = this.props.user;

        const debug = false;

        if (currentContainer === '') return;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:loadCurrentContainer(${currentContainer})`);
        debug && console.log(`##################################################\n\n`);

        let portfolioLocalStorage = [];

        // = SETTINGS  ==============================================================
        // 
        // Einstellungen aus IndexedDB laden 
        // 
        // ==========================================================================
        const containerDownload = utils.getDownload(currentContainer);

        if (containerDownload) {
            this.props.setSettingList(containerDownload.settings);

            this.props.setTagSettings(containerDownload.settings.tags);

            this.props.setIconList(containerDownload.icons);
        }

        // = CHECK APP MODUS ===========================================================
        // 
        // 1. Check: "appPreview === false"
        //    Wechselt ein "Admin"-User in den Prview-Mode "appPreview=true", dann wird
        //    implizit in den "user"-Modus gewechselt.
        //
        // 2. Check: "window['cordova'] !== undefined"
        //    Auf Apple-Geräten ist der ADMIN-Modus nicht erlaubt. In diesem Fall wird 
        //    immer in den "user"-Modus gewechselt.
        //           
        // =============================================================================       
        if (loginUser && loginUser.role === 'Admin' && appPreview === false) {
            // = ADMIN-MODE = (online-Mode) ================================================
            //               
            // =============================================================================            
            console.log('------------ ADMIN-MODUS ------------');
            this.props.setAppModus('Admin'); // appActions.js

            // =============================================================================            
            // Mit getIconList() werden sämtliche Icons in der 'draft'-Version
            // geladen. Die Icons stehen danach this.props.iconsList global zur 
            // =============================================================================            
            this.props.getIconList();

            // =============================================================================            
            // Mit getSettingList() werden sämtliche Katalogeinstellungen in der 'draft'-Version
            // geladen. Die draft settings stehen danach this.props.settingList global zur 
            // Verfügung. Die this.props.settingList wird dann vor allem im MoreController
            // im ProduktTagsModal benötigt, um die Katalogeinstellungen als Administrator
            // pflegen zu können.              
            // =============================================================================            
            this.props.getSettingList();

            // =============================================================================            
            // Mit getPortfolioList() werden sämtliche Katalogdaten in der 'draft'-Version
            // geladen. 
            // =============================================================================            
            if ((portfolioList && portfolioList.length === 0) || bForceReload) {
                debug && console.log('PortfolioList vom API laden');
                this.props.getPortfolioList(); // portfolioActions.js
            }
        }
        else {
            // = USER-MODE (offline-Mode) ==================================================
            // 
            // =============================================================================
            console.log('------------ USER-MODUS ------------');
            this.props.setAppModus('User'); // appActions.js

            // = SYNC ================================================================
            // 
            // portfolioDaten aus LocalStorage lesen 
            // 
            // ==========================================================================
            if ((portfolioList && portfolioList.length === 0) || bForceReload) {
                debug && console.log('Portfolio-Daten aus LocalStorage laden');

                this.props.resetPortfolio(); // portfolioActions.js
                this.props.setContentJSON([], []); // appActions.js

                await cordova.readLocalPortfolios(currentContainer).then(async (newPortfolioList) => {
                    newPortfolioList.forEach((d) => { portfolioLocalStorage.push(d) });

                    this.props.setPortfolioList(newPortfolioList); // portfolioActions.js

                    const productList = utils.makeProductList(newPortfolioList);

                    const tagList = utils.makeTagList(productList);

                    this.props.setContentJSON(productList, tagList); // appActions.js

                    // ----------------------------------------------------------------------------------
                    // ImageBuffer asynchron befüllen  #imageBuffer
                    // ----------------------------------------------------------------------------------
                    if (!imageBuffer[currentContainer] || imageBuffer[currentContainer]?.length === 0) {
                        cordova.loadImageBuffer(currentContainer, newPortfolioList);
                    }

                }).catch(() => {
                    console.log('FEHLER: Portfolios konnten nicht vollständig geladen werden!');
                    return;
                })
            }

            // = CUSTOM-LAYOUT ==========================================================
            // 
            // customLayout.json aus IndexedDB lesen 
            // 
            // ==========================================================================  
            if (portfolioLocalStorage.length > 0) {
                debug && console.log('customLayout.json laden');

                await cordova.readLocalJSON(currentContainer, 'customLayout.json').then((customLayoutJSON) => {
                    debug && console.log('   readLocalJSON(customLayout.json) success:', customLayoutJSON);

                    if (portfolioLocalStorage.length > customLayoutJSON.length) {
                        console.log('MasterLayout changed! -> ignore customLayout!');
                        console.log('TODO: better delete customLayout in dexie');
                    } else {
                        this.props.setCustomLayout(customLayoutJSON);
                    }

                }).catch((error) => {
                    debug && console.log('   WARNUNG: PortfoliosController: customLayout.json existiert noch nicht!', error);
                });
            }

            // = FAVORITTEN ==============================================================
            // 
            // favored.json aus IndexedDB laden
            // 
            // ==========================================================================
            if (portfolioLocalStorage.length > 0) {
                debug && console.log('favored.json laden');

                await cordova.readLocalJSON(currentContainer, 'favored.json').then((favourJSON) => {
                    debug && console.log('   readLocalJSON(favored.json) success:', favourJSON);
                    this.props.setFavoredList(favourJSON);
                }).catch((error) => {
                    debug && console.log('   WARNUNG: PortfoliosController: favored.json existiert noch nicht!', error);
                });
            }

            // = FILTER =================================================================
            // 
            // filter.json aus IndexedDB laden
            // 
            // ==========================================================================
            if (portfolioLocalStorage.length > 0) {
                debug && console.log('filter.json laden');

                await cordova.readLocalJSON(currentContainer, 'filter.json').then((filterJSON) => {
                    debug && console.log('  readLocalJSON(filter.json) success:', filterJSON);
                    this.props.setFilterList(filterJSON);
                }).catch((error) => {
                    debug && console.log('  WARNUNG: PortfoliosController: filter.json existiert noch nicht!', error);
                });
            }
        }
    }

    // ==========================================================================
    //
    // RENDER - SECTION
    //
    // ==========================================================================

    render() {
        const { currentContainer, portfoliosReady, appPreview } = this.props.app;
        const { progress, syncType } = this.props.sync;

        const renderConfirmUpload = () => {
            return (
                <>
                    <ConfirmModal dialog='ConfirmUpload' type={'success'} title="" onConfirm={() => this.props.closeModalDialog()}>
                        <div className="flex justify-start">{"Datei wurde erfolgreich hochgeladen."}</div>
                    </ConfirmModal>
                    <ConfirmModal dialog='ConfirmUpload' type={'error'} title="Fehler" onConfirm={() => this.props.closeModalDialog()}>
                        <div className="flex justify-start">{"Datei konnte nicht hochgeladen werden!"}</div>
                        <div className="flex justify-start">{"Uploadzeit (5 Minuten) wurde überschritten."}</div>
                        <div className="flex justify-start">{"Mögliche Gründe hierfür:"}</div>
                        <div className="flex justify-start">{"- Datei ist zu groß."}</div>
                        <div className="flex justify-start">{"- Server ist evtl. überlastet."}</div>
                    </ConfirmModal>
                    <ConfirmModal dialog='ConfirmUpload' type={'nameError'} title="Fehler" onConfirm={() => this.props.closeModalDialog()}>
                        <div className="flex justify-start">{"Datei konnte nicht hochgeladen werden!"}</div>
                        <div className="flex justify-start">{"Der Dateiname enthält Sonderzeichen"}</div>
                        <div className="flex justify-start">{"oder Umlaute. Bitte Dateiname ändern!"}</div>
                    </ConfirmModal>
                </>
            )
        }

        /**
         * Wenn der Administrator aus dem Edit-Mode in den Preview-Mode
         * wechselt, muss er bestätigen, dass er die draft-Version landen 
         * möchte. Die Funktion onDownloadSuccess() wird nun aufgerufen,  
         * wenn der Administrator das Laden bestätigt hat.
         */
        const onDownloadSuccess = () => {
            if (!appPreview) {
                this.props.setPath('');
                this.props.setAppPreview(!appPreview);
            }
        }

        if (!portfoliosReady) { return <SplashScreen /> }

        if (currentContainer === '') {
            console.log(this.component + ': !currentContainer -> redirect to /containers\n\n`');
            return <Redirect to='/containers' />
        }

        return (
            <div className="w-full h-full flex flex-col">
                <Header />
                <Portfolio />
                <GroupController />
                <ProductControllerUser />
                <ProductControllerAdmin />
                <ProductLandscape />
                <ProductPortrait />
                <SearchController />
                <ProgressView />
                <ProgressBar />
                <FilterModal />
                <FavoredController />
                {(syncType !== '' && progress !== null) ? null : <Footer />}

                <GroupModal />
                <ProductModal />
                <PortfolioModal />
                <ProductTagsModal />
                <ProductDetailsModal />
                <DownloadConfirmModal onSuccess={onDownloadSuccess} />
                <WebsiteModal />
                <ImageTagsModal />
                <ImageGalleryModal />
                <MediaModal />
                <DebugModal />
                <UploadModal />
                {renderConfirmUpload()}
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        app: state.app,
        user: state.user,
        icon: state.icon,
        sync: state.sync,
        version: state.version,
        settings: state.settings,
        container: state.container,
        portfolio: state.portfolio
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(Object.assign({}, appActions, userActions, iconActions, syncActions, versionActions, containerActions, portfolioActions, settingActions), dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PortfoliosController);